import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import HTMLReactParser from 'html-react-parser'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { PerfectCenter, FlexContainer, HR, flattenObject, useWindowSize, Button, PureSpinner } from 'heliocor-ui'
import 'styled-components/macro'

import Logo from '../Logo'
import { RELEASE_NUM } from '../../config/domain'
import { SET_STEP_INDEX, SET_STEPS_LIST, SET_OWNERS_WARNING_MIN_DOC, REQUEST_SET_NAVIGATION, SET_NOTIFICATION, SET_STEP_HAS_ERROR, REQUEST_SAVE_DATA_TO_LOCALSTORE } from '../../store/cafe/actionTypes'
import StepperWizard from './Stepper'
import LanguageSwitcher from '../LanguageSwitcher'
import { ActionFlexContainer, WizardFrame, Container, WizardContent, WizardHeader, PoweredLogo, CategoryDescription } from './styles'
import { Snackbar } from '../Snackbar/Snackbar'
import CommonTitle from '../CommonTitle'
import dokstorLogo from '../../assets/logos/poweredbydokstor.png'

import { checkMinDocsPartyHasBeenUploaded, checkMinDocsPersonHasBeenUploaded } from './helpers'
import { SaveAndReset, SupportEmail } from './StepperExtras'

const Wizard = ({ steps, saveDataToStore, formIsValid, cleanErrors, type, customNavigationButtons, companyLookUpInstructions,
    handleSubmit, values, errors, updateRequestedEntity, customValidations, postErrors, postErrorHandler, showKYCTypeStep }) => {

    const { t } = useTranslation()
    const dispatch = useDispatch()
    const saveDataToLocalStore = (stepType, cat, index) => dispatch({ type: REQUEST_SAVE_DATA_TO_LOCALSTORE, stepType, cat, index})

    const [lastStep, setLastStep] = useState()
    const [isLoading, setIsLoading] = useState(true)
    const [missingOwnerDocs, setMissingOwnerDocs] = useState(false)
    const [missingPersonDocs, setMissingPersonDocs] = useState(false)
    const [currentStepHasErrors, setCurrentStepHasErrors] = useState()
    const [navigationUpdated, setNavigationUpdated] = useState(false)

    const message = useRef()

    const customNavigationEnabled = useSelector(state => state.navigation.customNavigationEnabled)
    const notification = useSelector(state => state.general.notification)
    const authenticated = useSelector(state => state.config.authenticated)
    const bottomNavDisabled = useSelector(state => state.general.bottomNavigationDisabled)
    const backupStepIndex = useSelector(state => state.navigation.backupStepIndex)
    const stepIndex = useSelector(state => state.navigation.stepIndex)
    const stepsList = useSelector(state => state.navigation.stepsList)
    const cafeStatus = useSelector(state => state.general.cafeStatus)
    const appStatus = useSelector(state => state.general.appStatus)
    const settings = useSelector(state => state.settings)
    const party = useSelector(state => state.party)
    const isRequest = useSelector(state => state.config.isRequest)
    const connectedParties = useSelector(state => state.party.connectedParties)
    const kycType = useSelector(store => store.settings.kycType)

    const authVerified = useRef(false)
    const container = useRef()
    const refValues = useRef(values)

    const { isMobile } = useWindowSize()

    const setStepIndex = useCallback(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (stepIndex, completedStep) =>  dispatch({ type: SET_STEP_INDEX, stepIndex, completedStep}),
        [dispatch]
    )

    const setStepsList = useCallback(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (stepsList) => dispatch({ type: SET_STEPS_LIST, stepsList }),
        [dispatch]
    )

    const setNavigation = useCallback(
        () => dispatch({ type: REQUEST_SET_NAVIGATION }),
        [dispatch]
    )

    const setStepHasError = useCallback(
        (stepIndex) => dispatch({ type: SET_STEP_HAS_ERROR, stepIndex }),
        [dispatch]
    )

    const minimumDataLoaded = party.backupPartyLoaded && (!kycType || appStatus.fields === 'LOADED')

    //EFFECTS
    useEffect(() => {
        const backgroundColor = settings.colors.backgroundColor
        if (backgroundColor) document.body.style.backgroundColor = backgroundColor
    }, [settings.colors.backgroundColor])

    useEffect(() => {
        //Sets step to Verification if not authenticated
        if (!authenticated && !isEmpty(steps)) {
            setStepIndex(0)
            setIsLoading(false)

        //Sets step to the first in the list when authenticated and no backup (maintains loader while getting party data)
        } else if (authenticated && !authVerified.current && type !== 'VERIFICATION' && !backupStepIndex && minimumDataLoaded) {
            setStepIndex(1, 0)
            authVerified.current = true
            setNavigationUpdated(true)
            cafeStatus === 'PARTY_LOADED' && setIsLoading(false)

        //Sets step to the first in the list when authenticated and backup (maintains loader while getting party data)
        } else if (authenticated && !navigationUpdated && type !== 'VERIFICATION' && minimumDataLoaded) {
            const initStep = 1
            setStepIndex((backupStepIndex && backupStepIndex >= steps.length ? initStep : backupStepIndex) || initStep)
            authVerified.current = true
            setNavigationUpdated(true)
            cafeStatus === 'PARTY_LOADED' && setIsLoading(false)

        //If data and docs has been loaded ensures the loader disappears
        } else if (minimumDataLoaded && cafeStatus === 'PARTY_LOADED' && isLoading) {
            setIsLoading(false)
        }
        
        const _stepsList = steps.map(step => step.id)
        if (!isEqual(_stepsList, stepsList) && authenticated) {
            setStepsList(steps.map(step => step.id))
        }

    }, [authenticated, backupStepIndex, type, steps.length, stepsList, setStepIndex, setStepsList, navigationUpdated, cafeStatus, isLoading, kycType, minimumDataLoaded])

    useEffect(() => {
        const hasErrors = lastStep
            ? bottomNavDisabled || !formIsValid || (isRequest && missingOwnerDocs) || (!isRequest && missingPersonDocs) || !isEmpty(postErrors) || !settings?.entity
            : bottomNavDisabled || !formIsValid || (isRequest && stepsList[stepIndex] === 'STRUCTURE' && missingOwnerDocs) || (stepsList[stepIndex] === 'STRUCTURE' && !isEmpty(postErrors))

        setCurrentStepHasErrors(hasErrors)

        if (isRequest && hasErrors) setStepHasError(stepIndex)

    }, [isRequest, setStepHasError, stepsList, bottomNavDisabled, currentStepHasErrors, formIsValid, lastStep, missingOwnerDocs, missingPersonDocs, stepIndex, steps.length, values, postErrors, settings])

    useEffect(() => {
        if (steps) setLastStep((stepIndex === steps.length - 1) && (settings.kycType || isRequest))
    }, [stepIndex, lastStep, steps, settings.kycType, isRequest])

    useEffect(() => {
        if (notification && !isLoading) {
            message.current(notification)
        }
    }, [notification, isLoading])

    useEffect(() => {
        if (isRequest) setNavigation()
    }, [stepIndex, isRequest, setNavigation])

    useEffect(() => {
        const checkAllMinDocsUploaded = async () => {
            await checkMinDocsPartyHasBeenUploaded({
                connectedParties,
                settings,
                localCallback: setMissingOwnerDocs,
                sagaCallback: (identifiers) => dispatch({ type: SET_OWNERS_WARNING_MIN_DOC, identifiers })
            })
        }

        if ((stepsList[stepIndex] === 'STRUCTURE' && !customNavigationEnabled) || ['OWNER_UPDATED', 'OWNER_DELETED'].includes(cafeStatus)) {
            checkAllMinDocsUploaded()
        } else setMissingOwnerDocs(false)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIndex, customNavigationEnabled, cafeStatus])

    useEffect(() => {
        const checkMinDocsPerson = async () => {
            await checkMinDocsPersonHasBeenUploaded({
                party,
                settings,
                localCallback: setMissingPersonDocs,
                sagaCallback: (categories) => dispatch({
                    type: SET_NOTIFICATION,
                    notification: {
                        text: 'errors.missingDocsPerson',
                        type: 'warning'
                    }
                })
            })
        }

        if (isRequest && stepsList[stepIndex] === 'summary') checkMinDocsPerson()

    }, [stepIndex, customNavigationEnabled, cafeStatus, dispatch, isRequest, party, settings, stepsList])

    useEffect(() => {
        if (lastStep && postErrors) {
            container.current.scrollTop = container.current.scrollHeight
        }
    }, [lastStep, postErrors])

    function handleNext({ index }) {
        saveDataToStore()
        container.current?.scrollTo && container.current.scrollTo(0, 0)
        handleUpdateRequestedEntity()
        const { type, id } = steps[stepIndex]
        saveDataToLocalStore(type, id, index)
    }

    function handleBack() {
        setStepIndex(Math.max(0, stepIndex - 1)) 
        cleanErrors()
        container.current?.scrollTo && container.current.scrollTo(0, 0)
    }

    function handleFinish() {
        handleSubmit()
    }

    function handleUpdateRequestedEntity() {

        if (steps[stepIndex].sendToApi && !isEqual(flattenObject(refValues.current), flattenObject(values))) {
            let valuesToUpdate = {}
            if (steps[stepIndex].form && steps[stepIndex].form.props && steps[stepIndex].form.props.fields) {
                steps[stepIndex].form.props.fields.forEach(field => {
                    if (values[field.id] || values[field.id] === false) valuesToUpdate = {
                        ...valuesToUpdate,
                        [field.id]: values[field.id].value || values[field.id]
                    }
                })
            }
            if (!isEmpty(valuesToUpdate)) updateRequestedEntity(valuesToUpdate)
            refValues.current = values
        }
    }

    function handleSaveDataOnGoToStep() {
        if (!isEqual(flattenObject(refValues.current), flattenObject(values))) {
            saveDataToStore()
            refValues.current = values
        }
    }

    function goToStep(index) {
        setStepIndex(index, isEmpty(errors) && stepIndex)
        handleUpdateRequestedEntity()
        !isRequest && !customNavigationEnabled && handleSaveDataOnGoToStep()
    }
    
    let standardActions = [
        {
            id: 'button.back',
            onClick: handleBack,
            value: t('button.back'),
            type: 'light',
            margin: '0 16px 0 0',
            disabled: !authenticated || bottomNavDisabled || stepIndex === 1 || (showKYCTypeStep && stepIndex === 2)
        },

        {
            id: lastStep ? 'button.submit' : 'button.next',
            onClick: lastStep ? handleFinish : handleNext,
            value: lastStep ? t('button.submit') : t('button.next'),
            margin: '0 16px 0 0',
            disabled: !authenticated || currentStepHasErrors,
            isLoading: cafeStatus === 'LOADING'
        }
    ]

    const mainActions = {
        VERIFICATION: [],
        PERSON: standardActions,
        COMPANY: standardActions,
        CONNECTED_COMPANY: standardActions,
        CONNECTED_PERSON: standardActions
    }

    const categoryDescription = steps[stepIndex]?.description || ''

    const bottomNavActions = customNavigationEnabled ? customNavigationButtons : mainActions[type]

    const formSubmitted = cafeStatus === "SUBMITTED"

    return (
        <WizardFrame column>
            <FlexContainer responsive>
                <StepperWizard
                    steps={steps}
                    activeStep={stepIndex}
                    goToStep={goToStep}
                    saveDataToStore={saveDataToStore}
                    cleanErrors={cleanErrors}
                    errors={errors}
                    isMobile={isMobile}
                    setStepHasError={setStepHasError}
                    handleNext={handleNext}
                    showKYCTypeStep={showKYCTypeStep}
                />
                <WizardContent column>
                    <WizardHeader >
                        <Logo />
                        <LanguageSwitcher />
                    </WizardHeader>
                    <HR />              
                    {steps[stepIndex]?.title && (
                        <CommonTitle value={t(steps[stepIndex].title)} />
                    )}
                    {isLoading || !steps[stepIndex]
                        ?
                        <PerfectCenter><PureSpinner /></PerfectCenter>
                        :
                        <Container
                            ref={container}
                            id="wizard-container"
                            data-cy="wizard-container"
                        >
                            {categoryDescription && (
                                <CategoryDescription>
                                    {HTMLReactParser(categoryDescription)}
                                </CategoryDescription>
                            )}
                            {
                                steps[stepIndex].component
                                    ?
                                    React.createElement(
                                        steps[stepIndex].component,
                                        {
                                            ...steps[stepIndex].props,
                                            values,
                                            errors,
                                            wizardContainerRef: container,
                                            companyLookUpInstructions,
                                            customValidations,
                                            formattingOptions: settings.formattingOptions
                                        })
                                    : {
                                        ...steps[stepIndex].form,
                                        props: {
                                            ...steps[stepIndex].form.props,
                                            values,
                                            errors,
                                            handleNext,
                                            companyLookUpInstructions,
                                            customValidations,
                                            formattingOptions: settings.formattingOptions,
                                            postErrors,
                                            postErrorHandler
                                        }
                                    }
                            }
                        </Container>}
                </WizardContent>
                <Snackbar children={add => (message.current = add)} />
            </FlexContainer>
            <FlexContainer width='100%' backgroundColor='stepperBackground'>
                {!isMobile &&
                    <FlexContainer column justifyContent='center' alignItems='center' width={isMobile ? 'auto' : '192px'} padding='8px'>
                        <PoweredLogo alt='powered by heliocor' src={dokstorLogo} />
                        <p css='font-size: 10px; color: lightgrey; text-align: center; margin: 2px;'>{RELEASE_NUM}</p>
                    </FlexContainer>
                }
                <ActionFlexContainer justifyContent='space-beteewen' width='100%'>
                    <FlexContainer width='100%'>
                        {settings.supportEmail && <SupportEmail asIcon={isMobile} supportEmail={settings.supportEmail} />}
                        {isMobile && stepIndex !== 0 && <SaveAndReset saveDataToStore={saveDataToStore} asIcon />}
                    </FlexContainer>
                    {!formSubmitted && bottomNavActions.map((props, index) => <Button data-cy={props.id} key={index} {...props} />)}
                </ActionFlexContainer>
            </FlexContainer>
        </WizardFrame>
    )
}

export default Wizard